import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { useEmpresa } from "../utils/empresaActual";

const INTERVALO_COMPROBACION = 5 * 60 * 1000;
const KEY_VERSION = "catalogo_version_local";
const KEY_DIA = "catalogo_actualizado_dia";

const hoyTexto = () => new Date().toISOString().slice(0, 10);

export default function ActualizacionCatalogo({ onActualizado }) {
  const { erp } = useEmpresa();
  const [mostrarModal, setMostrarModal] = useState(false);
  const [motivo, setMotivo] = useState("nuevo"); // "nuevo" | "diario"
  const [actualizando, setActualizando] = useState(false);
  const versionLocal = useRef(null);

  const empresaId = (() => {
    try {
      const comercial = JSON.parse(sessionStorage.getItem("usuarioActivo"));
      if (comercial?.empresa_id) return comercial.empresa_id;
      const centro = JSON.parse(localStorage.getItem("centroActivo"));
      return centro?.empresa_id || null;
    } catch {
      return null;
    }
  })();

  const comprobarVersion = useCallback(async () => {
    if (!empresaId) return;

    // Cada empresa lleva su propia versión: antes era una fila global y
    // sincronizar el catálogo de una habría avisado a los comerciales de
    // todas las demás.
    const { data, error } = await supabase
      .from("catalogo_version")
      .select("version, actualizado_at, motivo")
      .eq("empresa_id", empresaId)
      .maybeSingle();

    if (error || !data) return;

    const versionServidor = data.version;

    if (versionLocal.current === null) {
      versionLocal.current = versionServidor;
      localStorage.setItem(KEY_VERSION, String(versionServidor));
      return;
    }

    if (versionServidor > versionLocal.current) {
      setMotivo("nuevo");
      setMostrarModal(true);
    }
  }, [empresaId]);

  useEffect(() => {
    const guardada = localStorage.getItem(KEY_VERSION);
    if (guardada) versionLocal.current = parseInt(guardada);

    // Actualización obligatoria diaria: el AS400 recalcula precios y stock
    // cada noche, así que una tablet que lleve encendida desde ayer trabaja
    // con datos viejos aunque la versión no haya llegado a saltar.
    if (localStorage.getItem(KEY_DIA) !== hoyTexto()) {
      setMotivo("diario");
      setMostrarModal(true);
    }

    comprobarVersion();

    const intervalo = setInterval(comprobarVersion, INTERVALO_COMPROBACION);
    return () => clearInterval(intervalo);
  }, [comprobarVersion]);

  const actualizar = async () => {
    setActualizando(true);

    if (empresaId) {
      const { data } = await supabase
        .from("catalogo_version")
        .select("version")
        .eq("empresa_id", empresaId)
        .maybeSingle();

      if (data) {
        versionLocal.current = data.version;
        localStorage.setItem(KEY_VERSION, String(data.version));
      }
    }

    if (onActualizado) await onActualizado();

    localStorage.setItem(KEY_DIA, hoyTexto());
    setActualizando(false);
    setMostrarModal(false);
  };

  if (!mostrarModal) return null;

  const esDiario = motivo === "diario";

  return (
    <div style={overlay}>
      <div style={modal}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>{esDiario ? "🔄" : "⚠️"}</div>
        <h2 style={{ fontSize: 22, fontWeight: "800", color: "#102A43", margin: "0 0 14px" }}>
          {esDiario ? "Actualización diaria" : "Actualización disponible"}
        </h2>
        <p style={{ fontSize: 15, color: "#333", lineHeight: 1.6, margin: "0 0 10px" }}>
          {esDiario
            ? `Cada noche se recalculan precios, stock y novedades desde el ${erp}. Actualiza para trabajar con los datos de hoy.`
            : `El catálogo de artículos o precios ha sido actualizado desde el ${erp}. Debes actualizar la app antes de continuar.`}
        </p>
        <p style={{ fontSize: 13, color: "#b3002d", fontWeight: "600", margin: "0 0 24px" }}>
          No podrás realizar pedidos hasta que la actualización se complete.
        </p>
        <button
          onClick={actualizar}
          disabled={actualizando}
          style={{
            width: "100%", padding: 16, borderRadius: 12, border: "none",
            background: "#0A84FF", color: "white", fontSize: 16,
            fontWeight: "800", cursor: "pointer",
            opacity: actualizando ? 0.7 : 1,
          }}
        >
          {actualizando ? "Actualizando..." : "Actualizar ahora"}
        </button>
      </div>
    </div>
  );
}

const overlay = {
  position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
  background: "rgba(10, 26, 43, 0.75)",
  display: "flex", alignItems: "center", justifyContent: "center",
  zIndex: 9999,
  padding: 16,
};

const modal = {
  background: "white", borderRadius: 20, padding: "36px 30px",
  maxWidth: 440, width: "100%", textAlign: "center",
  boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
};
